/* eslint-disable import/prefer-default-export,react-hooks/exhaustive-deps */
import { useRequestAccount, useWalletAPIClient } from '@ledgerhq/wallet-api-client-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import { cryptocurrenciesById } from '@ledgerhq/cryptoassets';
import BigNumber from 'bignumber.js';
import { SETTINGS_SUPPORTED_CURRENCIES_QUERY_KEY } from '../sharedQueries';
import { NETWORK_FEES_TYPES, walletCurrenciesTypes } from '../../screens/Accounts/constants';
import { showAlert } from '../notifications';
import { useCurrency } from './settings';
import { ADD_HARD_WALLET_ID_TO_WALLET } from '../sharedMutations';
import { transactions } from '../../api';

const LEDGER_FUNDING_PROVIDER = 'Baanx';

const LEDGER_CURRENCY_LIST_QUERY_KEY = 'ledger.currency.list';

const LEDGER_CURRENCY_IDS = [
  'bitcoin',
  'ethereum',
  'litecoin',
  'solana',
  'ripple',
  'bitcoin_cash',
  'ethereum_classic',
  'tezos',
  'ethereum/erc20/usd_tether__erc20_',
  'ethereum/erc20/usd__coin',
  'ethereum/erc20/baanx',
  'ethereum/erc20/baanx_old',
  'ethereum/erc20/uniswap',
  'ethereum/erc20/1inch_token',
  'ethereum/erc20/euro_coin',
  'ethereum/erc20/euro_tether',
  'ethereum/erc20/stasis_eurs_token',
  'ethereum/erc20/reef_finance',
  'ethereum/erc20/rio_fuel_token',
  'ethereum/erc20/wrapped_ust_token',
];

const LEDGER_ACCOUNT_LIST_QUERY_KEY = 'ledger.account.list';

const useLedgerWalletAccounts = () => {
  const { client } = useWalletAPIClient();

  const { data, isLoading, error } = useQuery(
    LEDGER_ACCOUNT_LIST_QUERY_KEY,
    () => client.account.list({ currencyIds: LEDGER_CURRENCY_IDS }),
    { enabled: Boolean(client) }
  );

  return {
    accounts: data || null,
    loading: isLoading,
    error,
  };
};

const useLedgerCurrencies = () => {
  const { client } = useWalletAPIClient();

  const { data, isLoading, error } = useQuery(
    LEDGER_CURRENCY_LIST_QUERY_KEY,
    () => client.currency.list({ currencyIds: LEDGER_CURRENCY_IDS }),
    { enabled: Boolean(client) }
  );

  return {
    currencies: data || null,
    loading: isLoading,
    error,
  };
};

export const useLedgerAccounts = (allAsArray = false) => {
  const { accounts, loading, error } = useLedgerWalletAccounts();

  const { currencies } = useLedgerCurrencies();

  const [result, setResult] = useState([{}, true]);
  const { data: enabledCurrencies, isLoading } = useQuery([
    SETTINGS_SUPPORTED_CURRENCIES_QUERY_KEY,
    walletCurrenciesTypes.CRYPTO,
    'currency_name',
  ]);

  useEffect(() => {
    if (accounts && currencies && !loading && !error && !isLoading && enabledCurrencies) {
      const _accounts = {};
      const accountsArray = [];
      const currenciesDetailsLedgerFormatted = {};

      const ledgerTokensList = currencies?.filter(
        (currency) => currency?.parent === cryptocurrenciesById.ethereum.id
      );

      Object.keys(enabledCurrencies).forEach((currencyName) => {
        const ledgerCurrencyName = enabledCurrencies[currencyName]?.is_erc20_token
          ? enabledCurrencies[currencyName].currency_code?.toUpperCase()
          : '';

        const ledgerCurrencyToken = ledgerTokensList.find(
          (item) => item.ticker === ledgerCurrencyName
        );

        enabledCurrencies[currencyName].ledgerCurrency = ledgerCurrencyToken?.id;

        const currencyNameLedgerFormatted =
          ledgerCurrencyToken?.id || currencyName.toLowerCase().replace(/ /g, '_');

        currenciesDetailsLedgerFormatted[currencyNameLedgerFormatted] =
          enabledCurrencies[currencyName];
      });

      accounts.forEach((account) => {
        const currencyDetails = currenciesDetailsLedgerFormatted[account.currency.toLowerCase()];
        if (currencyDetails && !allAsArray) {
          _accounts[currencyDetails.currency_code] = account;
        } else if (allAsArray) {
          const item = {
            name: account.name,
            coin: currencyDetails ? currencyDetails?.currency_code : account.currency.toLowerCase(),
            account,
          };
          accountsArray.push(item);
        }
      });

      if (allAsArray) {
        setResult([accountsArray, false]);
      } else {
        setResult([_accounts, false]);
      }
    }
  }, [enabledCurrencies, accounts, isLoading, loading, error, currencies, allAsArray]);

  return result;
};

export const useLedgerAccount = (coin, promptCreateAccount) => {
  const [result, setResult] = useState([[], false, null]);
  const currency = useCurrency(coin);

  const { accounts: _accounts, loading, error } = useLedgerWalletAccounts();
  const { currencies } = useLedgerCurrencies();

  const { requestAccount, account: newAccount } = useRequestAccount();

  let currencyName = currency ? currency.currency_name : null;

  useEffect(() => {
    if (!currencyName) {
      setResult([[], false, null]);
    } else if (_accounts && !loading && !error && currencies) {
      let selectedAccount = _accounts.filter(
        (account) => account.currency.toLowerCase() === currencyName.toLowerCase()
      );

      if (currency.is_erc20_token) {
        const ledgerTokensList = currencies?.filter(
          (currencyItem) => currencyItem?.parent === cryptocurrenciesById.ethereum.id
        );

        currencyName = currency.currency_code?.toUpperCase();

        const ledgerCurrencyToken = ledgerTokensList.find((item) => item.ticker === currencyName);

        selectedAccount = _accounts.filter(
          (account) => account.currency.toLowerCase() === ledgerCurrencyToken.id
        );
      }

      if (selectedAccount.length === 0 && promptCreateAccount) {
        requestAccount();
      } else {
        setResult([selectedAccount, false, null]);
      }
    } else if (error) {
      setResult([[], false, error]);
    }
  }, [currencyName, _accounts, loading, error, currencies]);

  useEffect(() => {
    if (newAccount) {
      setResult([[newAccount], false, null]);
    }
  }, [newAccount]);

  return result;
};

export const useLedgerAccountById = (id) => {
  const [result, setResult] = useState([null, true]);

  const { accounts, loading, error } = useLedgerWalletAccounts();

  useEffect(() => {
    if (!id) {
      setResult([null, false]);
    } else if (accounts && !loading) {
      if (error) {
        showAlert({ message: error.message, type: 'danger' });
      } else {
        const _account = accounts.find((account) => account.id === id);
        setResult([_account, false]);
      }
    }
  }, [accounts, loading, error, id]);

  return result;
};

export const useLedgerSupportedCurrencies = () => {
  const { currencies, loading, error } = useLedgerCurrencies();

  return {
    data: currencies,
    isLoading: loading,
    error,
  };
};

export const useLedgerTransaction = (baanxAccount, currency) => {
  const { currencies } = useLedgerCurrencies();

  const { client } = useWalletAPIClient();

  const { mutate: addHardWalletId } = useMutation(ADD_HARD_WALLET_ID_TO_WALLET);

  const [isLoading, setIsLoading] = useState(false);

  const potentialLedgerCurrencies = useMemo(() => {
    if (currencies) {
      return currencies?.filter((currencyItem) => {
        if (currency?.is_erc20_token && currencyItem?.parent) {
          const name = currency.currency_code?.toUpperCase();

          return (
            currencyItem?.parent === cryptocurrenciesById.ethereum.id &&
            currencyItem?.ticker === name
          );
        }

        // This change is for testing new ledger_name column and delete LEDGER_BAANX_CURRENCY_NAME_MAP
        // return currencyItem?.id === LEDGER_BAANX_CURRENCY_NAME_MAP[currency?.currency_code];
        return currencyItem?.id === currency?.ledger_name;
      });
    }
    return [];
  }, [currencies, currency]);

  const sendTransaction = useCallback(
    async (amount, feeStrategy = NETWORK_FEES_TYPES.Fast, senderAddress = '') => {
      setIsLoading(true);

      try {
        const currencyIds = potentialLedgerCurrencies?.map((item) => item.id) || [];

        const selectedAccount = await client.account.request({
          currencyIds,
        });

        const ledgerCurrency = potentialLedgerCurrencies.find(
          (item) => item.id === selectedAccount?.currency
        );

        const inAmountBNLedger = new BigNumber(amount).shiftedBy(ledgerCurrency?.decimals);

        const transactionId = await client.exchange.start('FUND');

        let inAddress = baanxAccount.address;
        if (ledgerCurrency?.family === 'ripple' || ledgerCurrency?.parent === 'ripple') {
          const addressArr = baanxAccount?.address?.split('?dt=');
          // eslint-disable-next-line prefer-destructuring
          inAddress = addressArr[0];
        }

        const { binaryPayload, signature } = await transactions.fund({
          transactionId,
          inAmount: inAmountBNLedger?.toNumber(),
          currency: currency.currency_code,
          inAddress,
        });

        let hexBinaryPayload = '';
        let hexSignature = '';
        if (binaryPayload && signature) {
          const binaryPayloadUint8Array = new Uint8Array(binaryPayload?.data);
          hexBinaryPayload = Array.from(binaryPayloadUint8Array)
            .map((byte) => byte.toString(16).padStart(2, '0'))
            .join('');

          const signatureUint8Array = new Uint8Array(signature?.data);
          hexSignature = Array.from(signatureUint8Array)
            .map((byte) => byte.toString(16).padStart(2, '0'))
            .join('');
        }

        const transaction = {
          family: ledgerCurrency?.family || ledgerCurrency?.parent,
          amount: inAmountBNLedger,
          recipient: baanxAccount.address,
        };

        if (ledgerCurrency?.family === 'ripple' || ledgerCurrency?.parent === 'ripple') {
          const addressArr = baanxAccount?.address?.split('?dt=');
          // eslint-disable-next-line prefer-destructuring
          transaction.recipient = addressArr[0];
          transaction.tag = +addressArr[1];
        }

        // this change is only for Solana, Litecoin to complete add funds flow successfully
        if (
          ledgerCurrency?.family === 'solana' ||
          ledgerCurrency?.parent === 'solana' ||
          ledgerCurrency?.family === 'litecoin' ||
          ledgerCurrency?.parent === 'litecoin'
        ) {
          transaction.model = {
            kind: 'transfer',
            uiState: {},
            commandDescriptor: {
              command: {
                kind: 'transfer',
                sender: senderAddress,
                recipient: baanxAccount.address,
                amount: inAmountBNLedger,
                memo: '',
              },
              fee: 5000,
              warnings: {},
              errors: {},
            },
          };
        }

        await client.exchange.completeFund({
          provider: LEDGER_FUNDING_PROVIDER,
          fromAccountId: selectedAccount.id,
          transaction,
          binaryPayload: hexBinaryPayload,
          signature: hexSignature,
          feeStrategy: feeStrategy?.toUpperCase(),
        });

        setIsLoading(false);
        addHardWalletId({
          wallet_address: baanxAccount.address,
          hard_wallet_id: selectedAccount.id,
        });

        return { success: true };
      } catch (err) {
        setIsLoading(false);
        return { success: false };
      }
    },
    [potentialLedgerCurrencies, baanxAccount, currency, client]
  );

  return {
    sendTransaction,
    isLoading,
  };
};
